import cn from 'classnames';
import { getWSSPriceFromTypeByRegion } from 'common/meal-plan/ConsumerPrices';
import { isMealPlanDessert } from 'common/MealPlanUtils';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { MealPlanQuantityPicker } from 'src/pages/consumer/meal-plans/checkout/MealPlanQuantityPicker';
import { generateThumborImgUrl } from 'src/shared/design-system/DynamicImage/utils';
import { useMealPlanForUserStoreSelectors } from 'src/ztore/wss/meal-plan-for-user-store';
import { PriceChip, ServingChip } from '.';
import { DishCardDisplayMode, MealPlanFoodItem } from '../../checkout/types';
import { FoodItemChip } from '../Chips/FoodItemChip';
import { IngredientsFlaggedChip } from '../Chips/IngredientsFlaggedChip';
import { MerchandisingTagChip } from '../Chips/MerchandisingTagChip';
import { isUpsellTierEligibleForFoodType, UpsellLabelChip } from '../Chips/UpsellChips';
import { FoodItemRatings } from '../FoodItemRatings';
import { SelectedQuantityDisplay } from './SelectedQuantityDisplay';

interface IProps {
  displayMode: DishCardDisplayMode;
  foodItem: MealPlanFoodItem;
  hideServingAndPricing?: boolean;
  onClick?: () => void;
  quantity?: number;
  displayServings: number;
  showShef: boolean;
  showNewFreshTag?: boolean;
  favoriteHeart?: React.ReactElement;

  // Cart-mode props (optional)
  buttonsDisabled?: boolean;
  remainingAvailableUnits?: number;
  onIncrement?: () => void;
  onDecrement?: () => void;
  quantityPicker?: React.ReactNode;
  showFlexServingsStepper?: boolean;
}

/**
 * The small meal plan dish card is used to display side and add-on dishes in the meal plan.
 * If quantityPicker is provided, the card will be in cart mode.
 */
export const MealPlanDishCardSmall: React.FC<IProps> = ({
  displayMode,
  foodItem,
  showShef,
  displayServings: _displayServings,
  hideServingAndPricing,
  onClick,
  quantity = 0,
  showNewFreshTag,
  favoriteHeart,

  buttonsDisabled = false,
  remainingAvailableUnits = Infinity,
  onIncrement,
  onDecrement,
  quantityPicker,
  showFlexServingsStepper = false,
}) => {
  const isCartEnabled = !!onIncrement && !!onDecrement;
  const upsellsEnabled = useFeatureFlag('upsellsEnabled');
  const { getMealPlanForUser } = useMealPlanForUserStoreSelectors();
  const { zipCode } = getMealPlanForUser() ?? {};
  const regionId = zipCode?.regionId;

  const {
    shef,
    name,
    id,
    ratingCount,
    averageRating,
    ingredientsFlagged,
    customIngredientsFlagged,
    merchandisingTags,
    imageUrl,
    mealPlanFoodType,
    upsell,
  } = foodItem;
  const shefName = shef.firstName;
  const displayServingsPerMeal = 1;
  const isDessert = isMealPlanDessert(mealPlanFoodType);

  const price = getWSSPriceFromTypeByRegion(mealPlanFoodType ?? '', regionId ?? 0) + (upsell?.price ?? 0);

  const foodItemChip = (
    <div className='absolute left-2 top-2'>
      <FoodItemChip remainingAvailableUnits={remainingAvailableUnits} unitsInCart={quantity} />
    </div>
  );
  const ingredientsFlaggedChipEl = ingredientsFlagged && (
    <IngredientsFlaggedChip
      ingredientsFlagged={ingredientsFlagged}
      customIngredientsFlagged={customIngredientsFlagged ?? []}
      showIngredientName={false}
    />
  );
  // Negative offsets cancel the heart's 48px tap target so the 32px icon hugs the corner.
  const topRightStack = (favoriteHeart || ingredientsFlaggedChipEl) && (
    <div className={cn('absolute flex items-center gap-1', favoriteHeart ? '-right-1 -top-1' : 'right-2 top-2')}>
      {ingredientsFlaggedChipEl}
      {favoriteHeart}
    </div>
  );
  const showUpsellChip = upsellsEnabled && !!upsell && isUpsellTierEligibleForFoodType(upsell.tier, mealPlanFoodType);
  const cornerTagChip = (
    <div className='absolute bottom-2 right-2'>
      {showUpsellChip ? (
        <UpsellLabelChip upsell={upsell} />
      ) : (
        <MerchandisingTagChip merchandisingTags={merchandisingTags ?? []} showNewFreshTag={showNewFreshTag} />
      )}
    </div>
  );
  const imageSrc = generateThumborImgUrl({ src: imageUrl ?? '', width: 149, height: 154 });

  const dishImage = (
    <div
      className={cn('flex h-full w-[138px] items-end bg-cover bg-center p-2')}
      style={{ backgroundImage: `url(${imageSrc})` }}>
      <>{foodItemChip}</>
      {topRightStack}
      {cornerTagChip}
    </div>
  );

  return (
    <div
      onClick={onClick}
      className={cn(
        'box-border flex h-[148px] gap-4',
        'overflow-hidden rounded-lg border border-neutral-100 bg-white pr-2 shadow-md',
        {
          'w-[330px] shrink-0': displayMode === DishCardDisplayMode.Carousel,
          'w-full': displayMode === DishCardDisplayMode.Grid,
          'cursor-pointer': !!onClick,
        }
      )}>
      <div className='relative'>{dishImage}</div>

      <div className='flex w-full flex-col justify-between self-stretch pb-2 pt-3'>
        <section className='flex flex-col gap-1'>
          <p className='m-0 line-clamp-2 text-ellipsis text-body-600 text-eggplant'>{name}</p>
        </section>
        <section className='flex w-full flex-col gap-1'>
          <FoodItemRatings ratingCount={ratingCount} averageRating={averageRating} foodItemId={id} />
          {showShef && (
            <p className='m-0 flex items-center gap-1 text-label-700 text-eggplant'>
              <span>Shef</span>
              <span className='line-clamp-1 font-quick-writers text-h6 text-raspberry'>{shefName}</span>
            </p>
          )}
          {!hideServingAndPricing && <hr className='mb-0.5' />}
          {showFlexServingsStepper ? (
            <div className='flex w-full flex-row items-center gap-4 pr-1'>
              {!hideServingAndPricing && <PriceChip price={price} />}
              <div className='flex min-w-0 flex-1 justify-end'>
                {quantityPicker || (quantity > 0 && <SelectedQuantityDisplay quantity={quantity} />)}
              </div>
            </div>
          ) : (
            <div className='flex w-full flex-row justify-between pr-1'>
              {!hideServingAndPricing && (
                <div className='flex gap-1'>
                  {!isDessert && <ServingChip numServings={displayServingsPerMeal} />}
                  <PriceChip price={price} />
                </div>
              )}
              {isCartEnabled ? (
                <MealPlanQuantityPicker
                  outline={false}
                  value={quantity}
                  disabled={buttonsDisabled}
                  onIncrement={onIncrement}
                  onDecrement={onDecrement}
                />
              ) : (
                quantity > 0 && <SelectedQuantityDisplay quantity={quantity} />
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
